import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import "./ProfileDashboard.css";
import ProfileUploadModal from "./ProfileUploadModal";
import { useAuth } from "../App";
import { apiFetch } from "../utils/apifetch";

/* ================= HELPERS ================= */

const buildFileUrl = (path) => {
  if (!path) return "/placeholder-user.png";

  const normalizedPath = path
    .replace(/\\/g, "/")
    .replace(/^\/+/, "")
    .replace(/\/{2,}/g, "/");

  const baseUrl = import.meta.env.VITE_API_URL.replace(/\/api\/?$/, "");
  return `${baseUrl}/${normalizedPath}`;
};

export default function ProfileDashboard() {
  const navigate = useNavigate();
  const { hasPermission, logout } = useAuth();

  /* ================= STATE ================= */

  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");
  const [sortField, setSortField] = useState("CreatedAt");
  const [sortDir, setSortDir] = useState("desc");

  /* MODAL STATE */
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("add"); // add | edit
  const [selectedProfile, setSelectedProfile] = useState(null);

  /* TOAST */
  const [toastMessage, setToastMessage] = useState("");

  /* ================= FETCH ================= */

  const fetchProfiles = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const res = await apiFetch("/profile", {}, logout, navigate);
      const data = await res.json();

      setProfiles(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      setError("Unable to load profiles");
    } finally {
      setLoading(false);
    }
  }, [logout, navigate]);

  useEffect(() => {
    fetchProfiles();
  }, [fetchProfiles]);

  /* ================= TOAST ================= */

  const showToast = (msg) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(""), 5000);
  };

  /* ================= DELETE ================= */

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this profile?")) return;

    try {
      await apiFetch(`/profile/${id}`, { method: "DELETE" }, logout, navigate);
      setProfiles((list) => list.filter((p) => p.Id !== id));
      showToast("Profile deleted successfully");
    } catch {
      alert("Unable to delete profile");
    }
  };

  /* ================= SEARCH + SORT ================= */

  const filteredProfiles = useMemo(() => {
    const q = search.toLowerCase();

    return profiles
      .filter((p) =>
        [p.ProfileName, p.ProfileTitle, p.CreatedAt]
          .join(" ")
          .toLowerCase()
          .includes(q)
      )
      .sort((a, b) => {
        const x = a[sortField] || "";
        const y = b[sortField] || "";
        if (x < y) return sortDir === "asc" ? -1 : 1;
        if (x > y) return sortDir === "asc" ? 1 : -1;
        return 0;
      });
  }, [profiles, search, sortField, sortDir]);

  /* ================= MODAL HANDLERS ================= */

  const openAddModal = () => {
    setModalMode("add");
    setSelectedProfile(null);
    setIsModalOpen(true);
  };

  const openEditModal = (profile) => {
    setModalMode("edit");
    setSelectedProfile(profile);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedProfile(null);
  };

  /**
   * status:
   *  - "updated"
   *  - "nochange"
   */
  const onProfileUploaded = (status = "updated") => {
    closeModal();

    if (status === "nochange") {
      showToast("Profile not changed");
      return;
    }

    fetchProfiles();

    showToast(
      modalMode === "edit"
        ? "Profile updated successfully"
        : "Profile uploaded successfully"
    );
  };

  /* ================= RENDER ================= */

  if (loading) return <div className="profile-loading">Loading profiles…</div>;
  if (error) return <div className="profile-error">{error}</div>;

  return (
    <div className="profile-dashboard">
      {/* TOAST */}
      {toastMessage && (
        <div className="toast-overlay">
          <div className="toast-success">{toastMessage}</div>
        </div>
      )}

      {/* HEADER */}
      <div className="profile-header">
        <div>
          <h2>Profile Library</h2>
          <p className="subtitle">Profiles of People</p>
        </div>

        {hasPermission("profile", "create") && (
          <button className="addButton" onClick={openAddModal}>
            ＋ Add Profile
          </button>
        )}
      </div>

      {/* TOOLBAR */}
      <div className="profile-toolbar">
        <input
          type="text"
          placeholder="Search profiles…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="toolbar-input"
        />

        <div className="sort-group">
          <select
            value={sortField}
            onChange={(e) => setSortField(e.target.value)}
            className="toolbar-select"
          >
            <option value="ProfileName">Name</option>
            <option value="ProfileTitle">Title</option>
            <option value="CreatedAt">Created Date</option>
          </select>

          <button
            className="toolbar-sort-btn"
            onClick={() =>
              setSortDir((d) => (d === "asc" ? "desc" : "asc"))
            }
          >
            {sortDir === "asc" ? "↑" : "↓"}
          </button>
        </div>
      </div>

      {/* GRID */}
      <div className="profile-grid">
        {filteredProfiles.map((p) => (
          <div className="profile-card" key={p.Id}>
            <div className="photo-wrapper">
              <img
                className="photo"
                src={buildFileUrl(p.PhotoPath)}
                alt={p.ProfileName}
              />
            </div>

            {p.ProfileTitle && (
              <span className="title">{p.ProfileTitle}</span>
            )}
            <span className="name">{p.ProfileName}</span>

            <div className="card-actions">
              <button onClick={() => navigate(`/profile/${p.Id}`)}>
                View
              </button>

              {hasPermission("profile", "update") && (
                <button className="edit" onClick={() => openEditModal(p)}>
                  Edit
                </button>
              )}

              {hasPermission("profile", "delete") && (
                <button
                  className="danger"
                  onClick={() => handleDelete(p.Id)}
                >
                  Delete
                </button>
              )}
            </div>
          </div>
        ))}

        {filteredProfiles.length === 0 && (
          <div className="empty">No profiles found</div>
        )}
      </div>

      {/* MODAL */}
      {isModalOpen && (
        <ProfileUploadModal
          mode={modalMode}
          profileData={selectedProfile}
          onClose={closeModal}
          onProfileUploaded={onProfileUploaded}
        />
      )}
    </div>
  );
}
